import { AuthorType, MessageType } from '../models';
import Resource from './Resource';

abstract class MercureResource<T> extends Resource {
  public baseURL?: string = process.env.REACT_APP_MERCURE_URL;
  public topicUrl: string = '';

  getMessage(
    id: string,
    topic: string,
    conversation: string,
    type: MessageType,
    message: string,
    conversationBetween: object,
    author?: string,
    authorIdentifier?: string,
    authorType?: string,
  ): URLSearchParams {
    const source: Record<string, string> = {
      topic,
      data: JSON.stringify({
        '@id': id,
        author,
        authorIdentifier,
        authorType,
        conversation,
        conversationBetween,
        date: new Date().toISOString(),
        message,
        type,
      }),
    };

    // Mercure event id for system notifications (leave, join, start, resume...), format
    // "<deterministicKey>#<unique>".
    //
    // <deterministicKey> = "<type>-<conversation>-<authorIdentifier>-<30s bucket>" is the DEDUP key: the
    // SSE consumer keys ON CONFLICT on it, so the same logical event published twice (the leave emitted
    // from two effects, a network retry, the StrictMode double-join) collapses to one sse_record, while a
    // genuinely later event (next bucket) stays distinct. Scoped to SYSTEM: customer/expert free text must
    // never be merged on identical content.
    //
    // #<unique> makes every published id UNIQUE so the consumer's Mercure stream can resume right after
    // the last event via Last-Event-ID, instead of the hub re-replaying its backlog on every reconnect
    // (which a non-unique id triggers). The consumer strips the "#..." suffix before deduping.
    if (authorType === AuthorType.SYSTEM) {
      const bucket = Math.floor(Date.now() / 30_000);
      source.id = `${type}-${conversation}-${authorIdentifier ?? ''}-${bucket}#${crypto.randomUUID()}`;
    }

    const data = new URLSearchParams();
    Object.entries(source).forEach(([key, value]) => data.append(key, value));

    return data;
  }

  /**
   * @param replacement pair of key:value to replace topic's infos in the endpoint
   */
  getTopic(replacement: any): string {
    let result = this.topicUrl;

    (Object.keys(replacement) as (keyof T)[]).forEach(key => {
      result = result.replace(
        new RegExp(`:${String(key)}`, 'g'),
        replacement[key],
      );
    });

    return result;
  }

  getTopicUrl(replacement: any): string {
    return this.getUrl() + this.getTopic(replacement);
  }

  async sendMessage(
    id: string,
    topicInfos: T,
    conversation: string,
    type: MessageType,
    message: string,
    author?: string,
    authorIdentifier?: string,
    authorType?: string,
    conversationBetween?: object,
  ): Promise<string> {
    if (conversationBetween === undefined) {
      conversationBetween = [];
    }
    const response = await fetch(
      `${this.getUrl()}`,
      Resource.getHeaders({
        method: 'POST',
        body: this.getMessage(
          id,
          this.getTopicUrl(topicInfos),
          conversation,
          type,
          message,
          conversationBetween,
          author,
          authorIdentifier,
          authorType,
        ),
      }),
    );

    return response.text();
  }
}

export default MercureResource;
